import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { WalkthroughOverlay } from "./walkthrough-overlay";
import { WALKTHROUGH_REGISTRY } from "./walkthrough-registry";
import {
  WalkthroughDefinition,
  WalkthroughHighlightStep,
  WalkthroughModalStep,
  WalkthroughStep,
} from "./walkthrough-types";

export interface WalkthroughContextValue {
  active: boolean;
  currentStep: WalkthroughStep | undefined;
  currentStepIndex: number;
  totalSteps: number;
  showMockReturns: boolean;
  showDetailOverlay: boolean;
  next(): void;
  back(): void;
  dismiss(): void;
  onMockReturnClicked(): void;
  onDetailClose(): void;
  onDetailProcessed(): void;
}

export const WalkthroughContext = createContext<
  WalkthroughContextValue | undefined
>(undefined);
WalkthroughContext.displayName = "WalkthroughContext";

const WALKTHROUGH_PARAM = "walkthrough";

function getWalkthroughLoader(
  id: string,
): (() => Promise<{ definition: WalkthroughDefinition }>) | undefined {
  return WALKTHROUGH_REGISTRY.find((entry) => entry.id === id)?.loader;
}

type ResolvedStep = WalkthroughModalStep | WalkthroughHighlightStep;

function isRenderable(step: WalkthroughStep): step is ResolvedStep {
  return step.type !== "detail-overlay" && step.type !== "close-detail-overlay";
}

function computeTotalSections(steps: WalkthroughStep[]): number {
  const sections = new Set<number>();
  for (const step of steps) {
    if (step.section) {
      sections.add(step.section.id);
    }
  }
  return sections.size;
}

function computeRenderableInfo(
  steps: WalkthroughStep[],
  currentStepIndex: number,
): { index: number; total: number } {
  let total = 0;
  let index = 0;
  for (const [i, step] of steps.entries()) {
    if (!isRenderable(step!)) {
      continue;
    }
    if (i === currentStepIndex) {
      index = total;
    }
    total++;
  }
  return { index, total };
}

export const WalkthroughProvider = memo(function WalkthroughProvider({
  children,
}: {
  children: ReactNode;
}) {
  const team = useContext(TeamContext);
  const teamLoaded = team !== undefined;
  const walkthroughEnabled =
    team?.featureFlags[FeatureFlagKey.MERCHANT_WALKTHROUGH] === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const rawWalkthroughParam = searchParams.get(WALKTHROUGH_PARAM);
  // Wait for team to load before resolving — prevents the loader from
  // firing before we know the flag status, and avoids stripping the
  // param prematurely.
  const walkthroughName =
    teamLoaded && walkthroughEnabled ? rawWalkthroughParam : null;

  // Strip the URL param once team has loaded and the flag is confirmed off
  useEffect(() => {
    if (teamLoaded && !walkthroughEnabled && rawWalkthroughParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(WALKTHROUGH_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [teamLoaded, walkthroughEnabled, rawWalkthroughParam, setSearchParams]);

  const [definition, setDefinition] = useState<
    WalkthroughDefinition | undefined
  >(undefined);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showDetailOverlay, setShowDetailOverlay] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loadedWalkthroughName, setLoadedWalkthroughName] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!walkthroughName) {
      // Don't clear an already-loaded walkthrough when React Router
      // navigation drops the search param from the URL.
      if (!loadedWalkthroughName) {
        setDefinition(undefined);
        setCurrentStepIndex(0);
        setDismissed(false);
      }
      return;
    }

    // Skip reloading if we already have this walkthrough loaded
    if (loadedWalkthroughName === walkthroughName) {
      return;
    }

    const loader = getWalkthroughLoader(walkthroughName);
    if (!loader) {
      return;
    }

    let cancelled = false;

    loader()
      .then((mod) => {
        if (!cancelled) {
          setDefinition(mod.definition);
          setCurrentStepIndex(0);
          setShowDetailOverlay(false);
          setLoadedWalkthroughName(walkthroughName);
        }
      })
      .catch((err) => {
        console.error(`Failed to load walkthrough "${walkthroughName}":`, err);
      });

    return () => {
      cancelled = true;
    };
  }, [walkthroughName, loadedWalkthroughName]);

  const removeParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(WALKTHROUGH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setLoadedWalkthroughName(undefined);
    setDefinition(undefined);
    setCurrentStepIndex(0);
    setShowDetailOverlay(false);
    removeParam();
  }, [removeParam]);

  const next = useCallback(() => {
    if (!definition) {
      return;
    }
    let nextIndex = currentStepIndex + 1;
    let overlayState: boolean | undefined;

    // Skip consecutive non-renderable steps, applying their side-effects
    while (nextIndex < definition.steps.length) {
      const s = definition.steps[nextIndex]!;
      if (s.type === "detail-overlay") {
        overlayState = true;
        nextIndex++;
      } else if (s.type === "close-detail-overlay") {
        overlayState = false;
        nextIndex++;
      } else {
        break;
      }
    }

    if (overlayState !== undefined) {
      setShowDetailOverlay(overlayState);
    }

    if (nextIndex >= definition.steps.length) {
      dismiss();
      return;
    }

    setCurrentStepIndex(nextIndex);
  }, [definition, currentStepIndex, dismiss]);

  const back = useCallback(() => {
    if (!definition) {
      return;
    }
    let prevIndex = currentStepIndex - 1;
    let overlayState: boolean | undefined;

    // Walk backwards, skipping steps we can't render:
    //  - detail-overlay / close-detail-overlay (non-renderable markers)
    //  - highlight steps whose target element isn't in the DOM (e.g. inside
    //    a process modal that's no longer open)
    while (prevIndex >= 0) {
      const s = definition.steps[prevIndex]!;
      if (s.type === "detail-overlay") {
        overlayState = false;
        prevIndex--;
        continue;
      }
      if (s.type === "close-detail-overlay") {
        overlayState = true;
        prevIndex--;
        continue;
      }
      if (s.type === "highlight") {
        try {
          if (!document.querySelector(s.targetSelector)) {
            prevIndex--;
            continue;
          }
        } catch {
          prevIndex--;
          continue;
        }
      }
      break;
    }

    if (prevIndex < 0) {
      return;
    }
    if (overlayState !== undefined) {
      setShowDetailOverlay(overlayState);
    }
    setCurrentStepIndex(prevIndex);
  }, [definition, currentStepIndex]);

  const onMockReturnClicked = useCallback(() => {
    // When user clicks a mock return row during interactive step, advance
    next();
  }, [next]);

  const handleDetailProcessed = useCallback(() => {
    if (!definition) {
      return;
    }
    // Advance past any non-renderable steps after the current one,
    // applying their side-effects
    let nextIndex = currentStepIndex + 1;
    let overlayState: boolean = false;
    while (nextIndex < definition.steps.length) {
      const s = definition.steps[nextIndex]!;
      if (s.type === "detail-overlay") {
        overlayState = true;
        nextIndex++;
      } else if (s.type === "close-detail-overlay") {
        overlayState = false;
        nextIndex++;
      } else {
        break;
      }
    }
    setShowDetailOverlay(overlayState);
    if (nextIndex < definition.steps.length) {
      setCurrentStepIndex(nextIndex);
    } else {
      dismiss();
    }
  }, [definition, currentStepIndex, dismiss]);

  const handleDetailClose = useCallback(() => {
    setShowDetailOverlay(false);
    if (!definition) {
      return;
    }
    // Scan backwards past non-renderable steps and highlight steps whose
    // target element isn't in the DOM (e.g. inside a modal that's closed).
    let target = currentStepIndex - 1;
    while (target >= 0) {
      const s = definition.steps[target]!;
      if (s.type === "detail-overlay" || s.type === "close-detail-overlay") {
        target--;
        continue;
      }
      if (s.type === "highlight") {
        try {
          if (!document.querySelector(s.targetSelector)) {
            target--;
            continue;
          }
        } catch {
          target--;
          continue;
        }
      }
      break;
    }
    if (target < 0) {
      return;
    }
    setCurrentStepIndex(target);
  }, [definition, currentStepIndex]);

  const currentRawStep = definition?.steps[currentStepIndex];
  const active = !!definition && !dismissed;

  const currentStep = active && currentRawStep ? currentRawStep : undefined;

  // Determine whether the returns table should show mock data
  const showMockReturns = useMemo(() => {
    if (!active) {
      return false;
    }
    return !!currentStep?.section?.showMockData;
  }, [active, currentStep]);

  const totalSections = useMemo(
    () => (definition ? computeTotalSections(definition.steps) : 0),
    [definition],
  );

  const renderableInfo = useMemo(
    () =>
      definition
        ? computeRenderableInfo(definition.steps, currentStepIndex)
        : { index: 0, total: 0 },
    [definition, currentStepIndex],
  );

  const resolved =
    currentStep && isRenderable(currentStep) ? currentStep : undefined;

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      active,
      currentStep,
      currentStepIndex,
      totalSteps: renderableInfo.total,
      showMockReturns,
      showDetailOverlay,
      next,
      back,
      dismiss,
      onMockReturnClicked,
      onDetailClose: handleDetailClose,
      onDetailProcessed: handleDetailProcessed,
    }),
    [
      active,
      currentStep,
      currentStepIndex,
      renderableInfo.total,
      showMockReturns,
      showDetailOverlay,
      next,
      back,
      dismiss,
      onMockReturnClicked,
      handleDetailClose,
      handleDetailProcessed,
    ],
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {active &&
        resolved &&
        createPortal(
          <WalkthroughOverlay
            back={back}
            dismiss={dismiss}
            next={next}
            step={resolved}
            stepIndex={renderableInfo.index}
            totalSections={totalSections}
            totalSteps={renderableInfo.total}
          />,
          document.body,
        )}
    </WalkthroughContext.Provider>
  );
});
